import { useCallback, useEffect, useRef, useState } from 'react'
import { AdminService } from '../lib/supabase/services/admin.service'
import type { AuthUser, ProfileRow } from '../lib/supabase/types/database.types'

export type AdminProfile = ProfileRow
export type AdminAccessStatus =
  | 'loading'
  | 'authorized'
  | 'unauthenticated'
  | 'inactive'
  | 'profile-missing'
  | 'error'
  | 'configuration-error'

interface AdminAccessState {
  status: AdminAccessStatus
  profile: AdminProfile | null
  user: AuthUser | null
  message: string | null
}

const initialState: AdminAccessState = {
  status: AdminService.isConfigured ? 'loading' : 'configuration-error',
  profile: null,
  user: null,
  message: AdminService.isConfigured ? null : AdminService.configurationError,
}

export function useAdminAccess() {
  const [state, setState] = useState<AdminAccessState>(initialState)
  const inactiveUserRef = useRef(false)
  const requestIdRef = useRef(0)

  const refreshAccess = useCallback(async () => {
    if (!AdminService.isConfigured) {
      setState(initialState)
      return
    }

    const requestId = requestIdRef.current + 1
    requestIdRef.current = requestId
    inactiveUserRef.current = false
    setState((current) => ({ ...current, status: 'loading', message: null }))

    try {
      const session = await AdminService.getSession()
      if (requestId !== requestIdRef.current) return

      if (!session) {
        setState({
          status: 'unauthenticated',
          profile: null,
          user: null,
          message: null,
        })
        return
      }

      const profile = await AdminService.getProfile(session.user.id)
      if (requestId !== requestIdRef.current) return

      if (!profile) {
        setState({
          status: 'profile-missing',
          profile: null,
          user: session.user,
          message:
            'Tu cuenta no tiene un perfil de acceso disponible. Contactá al administrador.',
        })
        return
      }

      if (profile.active === false) {
        inactiveUserRef.current = true
        await AdminService.signOut()
        if (requestId !== requestIdRef.current) return
        setState({
          status: 'inactive',
          profile,
          user: null,
          message: 'Usuario desactivado.',
        })
        return
      }

      setState({
        status: 'authorized',
        profile,
        user: session.user,
        message: null,
      })
    } catch (error) {
      if (requestId !== requestIdRef.current) return
      setState({
        status: 'error',
        profile: null,
        user: null,
        message:
          error instanceof Error
            ? 'No se pudo verificar tu sesión. Revisá tu conexión e intentá nuevamente.'
            : 'No se pudo validar la sesión. Intentá nuevamente.',
      })
    }
  }, [])

  useEffect(() => {
    void refreshAccess()
    if (!AdminService.isConfigured) return undefined

    return AdminService.subscribeToAuth(() => {
      if (!inactiveUserRef.current) void refreshAccess()
    })
  }, [refreshAccess])

  const signOut = useCallback(async () => {
    inactiveUserRef.current = false
    await AdminService.signOut()
    setState({
      status: 'unauthenticated',
      profile: null,
      user: null,
      message: null,
    })
  }, [])

  return { ...state, refreshAccess, signOut }
}
